import * as R from 'ramda';
import moment from 'moment';
import { handleActions } from 'redux-actions';

import { getUpdatedIds } from './helpers';
import * as commonReducers from './common';
import * as actions from '../actions/requests';
import { REQUESTS_LAYOUTS } from '../../api/constants';

import {
  deleteBudget,
  areArraysEqual,
  firstContainsSecond,
  calculateTotalBudget,
  isNeededResetDataSearch,
} from '../../utils';


const {
  create,
  update,
  fetch,
  remove,
} = commonReducers.operationTypes;


const getFormattedRequests = (state, requests, isFetching, project) =>
  Object.keys(requests).reduce((acc, key) => {
    const prevData = R.pick(
      ['meetingNotes', ...(isFetching ? ['order'] : [])],
      R.pathOr({}, ['items', key], state),
    );

    const newRequest = { ...prevData, ...requests[key], ...(!R.isEmpty(project) && { project }) };

    return R.assoc(key, newRequest, acc);
  }, {});


const fetchRequestsSuccessReducer = (
  state,
  {
    payload: {
      ids, requests = {}, project = {}, existsMore, notNewPage = false, count = 0, polling = false,
    } = {},
  },
) => ({
  ...state,
  count,
  [fetch]: false,
  page: notNewPage ? state.page : state.page + 1,
  existsMore: R.isNil(existsMore) ? state.existsMore : existsMore,
  ids: getUpdatedIds(state, ids, polling),
  items: { ...state.items, ...getFormattedRequests(state, requests, true, project) },
});

const searchRequestsFailureReducer =
  R.mergeDeepLeft({ [fetch]: false, ids: [], existsMore: false });

const searchRequestsSuccessReducer = (
  state,
  {
    payload: {
      existsMore, requests = {}, ids, count = 0, totalBudget = [], notNewPage, polling = false,
    } = {},
  },
) => ({
  ...state,
  count,
  totalBudget,
  [fetch]: false,
  page: notNewPage ? state.page : state.page + 1,
  existsMore: R.isNil(existsMore) ? state.existsMore : existsMore,
  ids: getUpdatedIds(state, ids, polling),
  items: { ...state.items, ...getFormattedRequests(state, requests) },
});

const createRequestSuccessReducer = (state, { payload }) => {
  const checkFilters = () => {
    const requestId = payload._id;

    let currentState = {
      ids: state.ids,
      count: state.count,
    };

    // do we need to show new request
    let showRequest = true;

    if (!R.isNil(payload.status) && !R.isEmpty(state.filters.statuses)
      && !state.filters.statuses.includes(payload.status)) {
      showRequest = false;
    }

    if (!R.isNil(payload.source) && !R.isEmpty(state.filters.sources)
      && !state.filters.sources.includes(payload.source)) {
      showRequest = false;
    }

    if (!R.isNil(payload.labels) && !R.isEmpty(state.filters.labels)
      && !firstContainsSecond(payload.labels, state.filters.labels)) {
      showRequest = false;
    }

    if (!R.isNil(payload.assignees) && !R.isEmpty(state.filters.assignees)
      && !firstContainsSecond(payload.assignees, state.filters.assignees)) {
      showRequest = false;
    }

    if (payload.requestDate && !R.isEmpty(state.filters.dateRange)) {
      const isAfter = moment(payload.requestDate, 'YYYY-MM-DD').isAfter(state.filters.dateRange[1]);
      const isBefore = moment(payload.requestDate, 'YYYY-MM-DD').isBefore(state.filters.dateRange[0]);

      if (isAfter && isBefore) {
        showRequest = false;
      }
    }

    if (showRequest) {
      currentState = {
        count: state.count + 1,
        ids: R.union([requestId], state.ids),
      };
    }

    return currentState;
  };

  return ({
    ...state,
    items: {
      ...state.items,
      [payload._id]: payload,
    },
    ...checkFilters(),
    [create]: false,
  });
};

const updateRequestReducer = (state, { payload: { requestId, modifier } }) => {
  const updatedState = R.converge(R.set, [
    R.always(R.lensPath(['items', requestId])),
    R.compose(
      R.omit(!modifier.project ? ['project'] : []),
      R.mergeDeepLeft(modifier),
      R.path(['items', requestId]),
    ),
    R.identity,
  ])(state);

  const checkFilters = () => {
    const prevRequestData = R.path(['items', requestId], state);

    let currentState = {
      ids: state.ids,
      count: state.count,
    };

    // if request doesn't match to filters
    const currentStateOnRequestRemove = {
      count: state.count - 1,
      ids: R.without(requestId, state.ids),
    };

    // if request matches to filters
    const currentStateOnRequestAdd = {
      count: state.count + 1,
      ids: R.union([requestId], state.ids),
    };

    // do we need to remove request's budget from total budget
    let removeBudget = false;

    if (!R.isNil(modifier.status)) {
      // if requestStatus doesn't match to filter
      if (!R.isNil(state.filters.statuses[0])
        && !R.equals(prevRequestData.status, modifier.status)) {
        // success optimistic update
        if (state.filters.statuses.includes(prevRequestData.status)
          && !state.filters.statuses.includes(modifier.status)) {
          removeBudget = true;
          currentState = currentStateOnRequestRemove;
        }
        // failed optimistic update
        if (!state.filters.statuses.includes(prevRequestData.status)
          && state.filters.statuses.includes(modifier.status)) {
          currentState = currentStateOnRequestAdd;
        }
      }
    }

    // if requestSource doesn't match to filter
    if (!R.isNil(modifier.source)) {
      if (!R.isNil(state.filters.sources) && !R.equals(prevRequestData.source, modifier.source)) {
        // success optimistic update
        if (state.filters.sources.includes(prevRequestData.source)
          && !state.filters.sources.includes(modifier.source)) {
          removeBudget = true;
          currentState = currentStateOnRequestRemove;
        }
        // failed optimistic update
        if (!state.filters.sources.includes(prevRequestData.source)
          && state.filters.sources.includes(modifier.source)) {
          currentState = currentStateOnRequestAdd;
        }
      }
    }

    if (!R.isNil(modifier.labels)) {
      // if requestLabels doesn't match to filter
      if (!R.isNil(state.filters.labels[0])
        && !areArraysEqual(prevRequestData.labels, modifier.labels)) {
        // success optimistic update
        if (firstContainsSecond(prevRequestData.labels, state.filters.labels)
          && !firstContainsSecond(modifier.labels, state.filters.labels)) {
          removeBudget = true;
          currentState = currentStateOnRequestRemove;
        }
        // failed optimistic update
        if (!firstContainsSecond(prevRequestData.labels, state.filters.labels)
          && firstContainsSecond(modifier.labels, state.filters.labels)) {
          currentState = currentStateOnRequestAdd;
        }
        if (!firstContainsSecond(modifier.labels, state.filters.labels)) {
          removeBudget = true;
        }
      }
    }

    if (!R.isNil(modifier.assignees)) {
      // if requestAssignees doesn't match to filter
      if (!R.isNil(state.filters.assignees[0])
        && !areArraysEqual(prevRequestData.assignees, modifier.assignees)) {
        // success optimistic update
        if (firstContainsSecond(prevRequestData.assignees, state.filters.assignees)
          && !firstContainsSecond(modifier.assignees, state.filters.assignees)) {
          removeBudget = true;
          currentState = currentStateOnRequestRemove;
        }
        // failed optimistic update
        if (!firstContainsSecond(prevRequestData.assignees, state.filters.assignees)
          && firstContainsSecond(modifier.assignees, state.filters.assignees)) {
          currentState = currentStateOnRequestAdd;
        }
      }
    }

    if (modifier.requestDate
      && !R.isNil(state.filters.dateRange[0])
      && prevRequestData.requestDate !== modifier.requestDate) {
      const isAfter = moment(modifier.requestDate, 'YYYY-MM-DD').isAfter(state.filters.dateRange[1]);
      const isBefore = moment(modifier.requestDate, 'YYYY-MM-DD').isBefore(state.filters.dateRange[0]);

      if (isAfter || isBefore) {
        removeBudget = true;
        currentState = currentStateOnRequestRemove;
      }
    }

    // update requests total budget
    const totalBudget = calculateTotalBudget(
      modifier.budget,
      prevRequestData.budget,
      state.totalBudget,
      removeBudget,
    );

    currentState = {
      ...currentState,
      totalBudget,
    };

    return currentState;
  };

  return {
    ...updatedState,
    ...checkFilters(),
    [update]: false,
  };
};

const addRequestMeetingNoteReducer = (
  state,
  { payload: { requestId, meetingNoteId } },
) => ({
  ...state,
  items: {
    ...state.items,
    [requestId]: {
      ...state.items[requestId],
      meetingNotes: R.union(
        R.pathOr([], ['items', requestId, 'meetingNotes'], state),
        [meetingNoteId],
      ),
    },
  },
});

const removeRequestMeetingNoteReducer = (
  state,
  { payload: { requestId, meetingNoteId } },
) => ({
  ...state,
  items: {
    ...state.items,
    [requestId]: {
      ...state.items[requestId],
      meetingNotes: R.without(
        [meetingNoteId],
        R.pathOr([], ['items', requestId, 'meetingNotes'], state),
      ),
    },
  },
});

const addRequestCommentReducer = (
  state,
  { payload: { requestId, commentId } },
) => ({
  ...state,
  items: {
    ...state.items,
    [requestId]: {
      ...state.items[requestId],
      comments: R.union(
        R.pathOr([], ['items', requestId, 'comments'], state),
        [commentId],
      ),
    },
  },
});

const removeRequestCommentReducer = (
  state,
  { payload: { requestId, commentId } },
) => ({
  ...state,
  items: {
    ...state.items,
    [requestId]: {
      ...state.items[requestId],
      comments: R.without(
        [commentId],
        R.pathOr([], ['items', requestId, 'comments'], state),
      ),
    },
  },
});

const removeRequestLogReducer = (
  state,
  { payload: { requestId, requestLogId } },
) => ({
  ...state,
  items: {
    ...state.items,
    [requestId]: {
      ...state.items[requestId],
      logs: R.without(
        [requestLogId],
        R.pathOr([], ['items', requestId, 'logs'], state),
      ),
    },
  },
});

const addRequestTaskReducer = (
  state,
  { payload: { requestId, taskId } },
) => {
  if (!requestId) {
    return state;
  }

  return ({
    ...state,
    items: {
      ...state.items,
      [requestId]: {
        ...state.items[requestId],
        tasks: R.union(
          R.pathOr([], ['items', requestId, 'tasks'], state),
          [taskId],
        ),
      },
    },
  });
};

const removeRequestTaskReducer = (
  state,
  { payload: { requestId, taskId } },
) => ({
  ...state,
  items: {
    ...state.items,
    [requestId]: {
      ...state.items[requestId],
      tasks: R.without(
        [taskId],
        R.pathOr([], ['items', requestId, 'tasks'], state),
      ),
    },
  },
});

const deleteRequestReducer = (state, { payload }) => (
  R.is(Array, payload)
    ? {
      ...state,
      [remove]: false,
      ids: R.without(payload, state.ids),
      items: R.omit(payload, state.items),
    }
    : {
      ...state,
      [remove]: false,
      count: state.count - 1,
      ids: R.without([payload], state.ids),
      items: R.dissoc(payload, state.items),
      ...(!R.isEmpty(state.items[payload].budget) &&
          { totalBudget: deleteBudget(state.items[payload].budget, state.totalBudget) }),
    }
);

const resetSearchDataReducer = (
  state,
  {
    payload: { prevSearchTerm = '', searchTerm = '' } = {},
  },
) => ({
  ...state,
  ...isNeededResetDataSearch(prevSearchTerm, searchTerm)
    ? {
      ids: [],
      page: 0,
      count: 0,
      existsMore: false,
      openMonthCard: null,
    }
    : {},
});

const setFilterReducer = (state, { payload: { filterType, data } }) => ({
  ...state,
  ...(((R.equals(filterType, 'statuses') && !R.equals(state.filters.statuses, data)) ||
  (R.equals(filterType, 'sources') && !R.equals(state.filters.sources, data)) ||
  (R.equals(filterType, 'labels') && !R.equals(state.filters.labels, data)) ||
  (R.equals(filterType, 'assignees') && !R.equals(state.filters.assignees, data)) ||
  (R.equals(filterType, 'dateRange') && !R.equals(state.filters.dateRange, data)) ||
  (R.equals(filterType, 'onlyFromCompanies') && !R.equals(state.filters.onlyFromCompanies, data)) ||
  ((R.equals(filterType, 'byLogHistory') && !R.equals(state.filters.byLogHistory, data))
    && state.filters.statuses && state.filters.dateRange))
    && {
      ids: [],
      items: {},
      page: 0,
      count: 0,
      existsMore: false,
      openMonthCard: null,
    }),
  filters: {
    ...state.filters,
    [filterType]: data,
    ...(state.filters.statuses
      && state.filters.dateRange
      && state.filters.byLogHistory
      && ((R.equals(filterType, 'statuses') || R.equals(filterType, 'dateRange')) && R.isEmpty(data)) && {
      byLogHistory: false,
    }),
  },
});

const resetFiltersReducer = state => ({
  ...state,
  filters: {
    labels: [],
    sources: [],
    statuses: [],
    assignees: [],
    dateRange: [],
    byLogHistory: false,
    onlyFromCompanies: false,
  },
});

const resetDataReducer = state => ({
  ...state,
  ids: [],
  page: 0,
  count: 0,
  existsMore: false,
  openMonthCard: null,
  ...commonReducers.defaultState,
});

const setNewCollapseKey = (state, { payload }) => {
  let updatedCollapseKey = payload.collapseKey || state.collapseKey;
  if (R.pathSatisfies(length => length === 0, ['arrayLength'], payload)) {
    updatedCollapseKey = null;
  }
  return {
    ...state,
    collapseKey: updatedCollapseKey,
  };
};

export const requestsReducers = handleActions(
  {
    // create
    [actions.createRequestRequest]: commonReducers.commonRequestReducer(create),
    [actions.createRequestFailure]: commonReducers.commonFailureReducer(create),
    [actions.createRequestSuccess]: createRequestSuccessReducer,

    // update
    [actions.updateRequestRequest]: commonReducers.commonRequestReducer(update),
    [actions.updateRequestFailure]: commonReducers.commonFailureReducer(update),
    [actions.updateRequestSuccess]: updateRequestReducer,

    // update (in modal window)
    [actions.updateRequestModalRequest]: commonReducers.commonRequestReducer(update),
    [actions.updateRequestModalFailure]: commonReducers.commonFailureReducer(update),
    [actions.updateRequestModalSuccess]: updateRequestReducer,

    // delete
    [actions.deleteRequestRequest]: commonReducers.commonRequestReducer(remove),
    [actions.deleteRequestFailure]: commonReducers.commonFailureReducer(remove),
    [actions.deleteRequestSuccess]: deleteRequestReducer,

    // fetch
    [actions.fetchRequestsRequest]: commonReducers.commonRequestReducer(fetch),
    [actions.fetchRequestsFailure]: commonReducers.commonFailureReducer(fetch),
    [actions.fetchRequestsSuccess]: fetchRequestsSuccessReducer,

    // search
    [actions.searchRequestsRequest]: commonReducers.commonRequestReducer(fetch),
    [actions.searchRequestsFailure]: searchRequestsFailureReducer,
    [actions.searchRequestsSuccess]: searchRequestsSuccessReducer,

    // Request's MeetingNotes
    [actions.addRequestMeetingNote]: addRequestMeetingNoteReducer,
    [actions.removeRequestMeetingNote]: removeRequestMeetingNoteReducer,

    // Request's Comments
    [actions.addRequestComment]: addRequestCommentReducer,
    [actions.removeRequestComment]: removeRequestCommentReducer,

    // Request's Logs
    [actions.removeRequestLog]: removeRequestLogReducer,

    // Request's Tasks
    [actions.addRequestTask]: addRequestTaskReducer,
    [actions.removeRequestTask]: removeRequestTaskReducer,

    // reset data
    [actions.resetRequestsData]: resetDataReducer,
    [actions.resetSearchDataRequests]: resetSearchDataReducer,

    // filters
    [actions.setFilter]: setFilterReducer,
    [actions.resetFilters]: resetFiltersReducer,

    [actions.setPage]: commonReducers.commonSetPageReducer,

    [actions.setCollapseKey]: setNewCollapseKey,

    [actions.setOpenMonth]: (state, { payload }) => R.assoc('openMonthCard', payload, state),

    [actions.changeRequestsLayout]: (state, { payload }) => R.assoc('layout', payload, state),
  },
  {
    ids: [],
    existsMore: true,
    openMonthCard: null,
    page: 0,
    filters: {
      statuses: [],
      sources: [],
      labels: [],
      assignees: [],
      dateRange: [],
      byLogHistory: false,
      onlyFromCompanies: false,
    },
    collapseKey: '6',
    count: 0,
    totalBudget: [],
    layout: REQUESTS_LAYOUTS.LIST,
    ...commonReducers.defaultState,
  },
);



// WEBPACK FOOTER //
// ./src/redux/reducers/requests.js